import { supabase } from '../lib/supabase';

// Helper function to extract first image URL from various formats
const getFirstImageUrl = (imageData) => {
  if (!imageData) return null;
  
  // If it's already a string URL
  if (typeof imageData === 'string') {
    try {
      // Try parsing as JSON array
      const parsed = JSON.parse(imageData);
      return Array.isArray(parsed) && parsed?.length > 0 ? parsed?.[0] : imageData;
    } catch {
      // Not JSON, return as-is
      return imageData;
    }
  }
  
  // If it's an array
  if (Array.isArray(imageData)) {
    return imageData?.length > 0 ? imageData?.[0] : null;
  }
  
  // If it's an object with url property
  if (imageData?.url) {
    return imageData?.url;
  }
  
  return null;
};

export const favoriteService = {
  async toggleFavorite(itemId, itemType) {
    const { data: { session } } = await supabase?.auth?.getSession();
    const user = session?.user;
    if (!user) throw new Error('Giriş yapmalısınız');

    const { data: existing } = await supabase?.from('favorites')?.select('id')?.eq('user_id', user?.id)?.eq('item_id', itemId)?.eq('item_type', itemType)?.maybeSingle();

    if (existing) {
      await supabase?.from('favorites')?.delete()?.eq('id', existing?.id);
      return false;
    } else {
      await supabase?.from('favorites')?.insert({ user_id: user?.id, item_id: itemId, item_type: itemType });
      return true;
    }
  },

  async checkIfFavorite(itemId, itemType) {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      const user = session?.user;
      if (!user) return false;
      const { data } = await supabase?.from('favorites')?.select('id')?.eq('user_id', user?.id)?.eq('item_id', itemId)?.eq('item_type', itemType)?.maybeSingle();
      return !!data;
    } catch (err) {
      console.warn('checkIfFavorite skipped:', err?.message);
      return false;
    }
  },

  // FIXED HYDRATION WITH STRICT MAPPING (snake_case -> camelCase)
  async getMyFavorites() {
    const { data: { session } } = await supabase?.auth?.getSession();
    const user = session?.user;
    if (!user) return [];

    const { data: favs } = await supabase?.from('favorites')?.select('*')?.eq('user_id', user?.id);
    if (!favs?.length) return [];

    const ids = { event: [], spot: [], product: [], marketplace: [] };
    favs?.forEach(f => { if (ids?.[f?.item_type]) ids?.[f?.item_type]?.push(f?.item_id); });

    const [events, spots, products, listings] = await Promise.all([
      ids?.event?.length ? supabase?.from('events')?.select('*')?.in('id', ids?.event) : { data: [] },
      ids?.spot?.length ? supabase?.from('spots')?.select('*')?.in('id', ids?.spot) : { data: [] },
      ids?.product?.length ? supabase?.from('products')?.select('*')?.in('id', ids?.product) : { data: [] },
      ids?.marketplace?.length ? supabase?.from('marketplace_items')?.select('*')?.in('id', ids?.marketplace) : { data: [] }
    ]);

    // 4. Merge and Format Data with Strict Mapping
    const combined = [
      // EVENTS - FIXED: Use event_date and getFirstImageUrl helper
      ...(events?.data || [])?.map(i => ({ 
        ...i, 
        itemType: 'event',
        title: i?.title,
        image: getFirstImageUrl(i?.image_url),  // Use helper function for image parsing
        date: i?.event_date,                     // CRITICAL FIX: Map event_date (not start_date)
        location: i?.location,
        price: i?.price
      })),
      // SPOTS - FIXED: Map name to title and use getFirstImageUrl
      ...(spots?.data || [])?.map(i => ({ 
        ...i, 
        itemType: 'spot',
        title: i?.name,                          // CRITICAL FIX: Map name to title
        image: getFirstImageUrl(i?.image_url),   // Use helper function
        location: i?.location,
        difficulty: i?.difficulty_level
      })),
      // PRODUCTS (Fix: Map 'name' to 'title')
      ...(products?.data || [])?.map(i => ({ 
        ...i, 
        itemType: 'product',
        title: i?.title || i?.name,
        image: getFirstImageUrl(i?.image_url),
        price: i?.price
      })),
      // MARKETPLACE
      ...(listings?.data || [])?.map(i => ({ 
        ...i, 
        itemType: 'marketplace',
        title: i?.title,
        image: getFirstImageUrl(i?.image_url),
        price: i?.price,
        location: i?.location
      }))
    ];
    return combined;
  }
};